import React, { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { CHAT_ICON, MENU_ICON, ZOOM_ICON } from "../../utills/image-constant";
import { overallHeatMap } from "../../services/api-services";
import HtmlZoomModal from "../htmlzoommodal";
import Modal from "../../admin/components/common/Model";
import Remark from "../../admin/components/Charts/Remark";

const Dimension = ({ selectedCompanyId, selectedCampaignId, type, graphName, title = "Overall Heatmap" }) => {
  const [heatmapData, setHeatMapData] = useState([]);
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [isAttempt, setIsAttempt] = useState("");
  const [colorRanges, setColorRanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [activeDimension, setActiveDimension] = useState(null);
  const modalRef = useRef(null);

  const [showTextBox, setShowTextBox] = useState(false);
  const [note, setNote] = useState("");
  const modalId = `zoomModal-${title.replace(/[^a-zA-Z0-9_-]/g, "_")}`;

  // Fetch heatmap data
  const getHeatmapData = async () => {
    setLoading(true);
    try {
      const response = await overallHeatMap(selectedCompanyId, selectedCampaignId, type, graphName);
      if (response?.data?.responseCode === 200) {
        const { responseBody, colorList } = response.data;
        console.log(response.data)
        setRemark(response?.data?.remark || "");
        setIsAttempt(response?.data?.isAttempt ?? 0);
        setRemarkId(response?.data?.remarkId || null);
        setHeatMapData(responseBody || []);
        setColorRanges(colorList || []);
      }
    } catch (err) {
      console.error("Error fetching heatmap data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getHeatmapData();
  }, [selectedCompanyId, selectedCampaignId, type, graphName]);

  const getColorForPercentage = (percentage) => {
    if (!colorRanges.length) return "#000000";
    for (const range of colorRanges) {
      if (percentage >= range.rangeFrom && percentage < range.rangeTo) {
        return range.colorName;
      }
    }
    return "#000000";
  };

  // Export to Excel
  const exportSummaryToExcel = () => {
    const titleRow = [["Overall Heatmap"]];
    const headers = ["Dimension/Sub-Dimension Name", "Count"];
    const rows = [];

    heatmapData.forEach((dimension) => {
      rows.push([dimension?.dimensionName, dimension?.percentage?.toFixed(2)]);
      dimension?.subDimensionOverallHeatmapChartResponseList?.forEach((sub) => {
        rows.push([`  - ${sub?.subDimensionName}`, sub?.percentage?.toFixed(2)]);
      });
    });

    const sheetData = [...titleRow, [], headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "Overall Heatmap.xlsx");
  };

  // Export to PDF
  const exportAsPDF = async () => {
    const chartBox = document.getElementById(`chart-box-${title}`);
    if (!chartBox) return;
    chartBox.scrollTo(0, 0);
    await new Promise((r) => setTimeout(r, 200));

    const chartElement = chartBox.querySelector(".apexcharts-canvas");
    if (!chartElement) return;

    const canvas = await html2canvas(chartElement, {
      scale: 2,
      backgroundColor: "#fff",
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth(); // Width of the PDF
    const pdfHeight = pdf.internal.pageSize.getHeight(); // Height of the PDF

    const canvasWidth = canvas.width; // Width of the canvas image
    const canvasHeight = canvas.height; // Height of the canvas image

    // Scaling factor to make the image a bit smaller (e.g., scale down by 80%)
    const scaleFactor =
      Math.min(pdfWidth / canvasWidth, pdfHeight / canvasHeight) * 0.8; // 0.8 reduces the size to 80%

    const imgWidth = canvasWidth * scaleFactor; // Scaled width
    const imgHeight = canvasHeight * scaleFactor; // Scaled height

    // Calculate the offset to center the image
    const xOffset = (pdfWidth - imgWidth) / 2;
    const yOffset = (pdfHeight - imgHeight) / 2;

    // Add the image to the PDF, centered and scaled down
    pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);

    // Save the PDF
    pdf.save(`${title || "chart"}.pdf`);
  };


const hideDropdownBeforeExport = () => {
    const dropdown = document.querySelector(".dropdown-menu.show");
    if (dropdown) dropdown.classList.remove("show");
  };
 const exportAsImage = async () => {
    hideDropdownBeforeExport();
    const chartBox = document.getElementById(`chart-box-${title}`);
    if (!chartBox) return;
    chartBox.scrollTo(0, 0);
    await new Promise(r => setTimeout(r, 200));

    const chartElement = chartBox.querySelector(".apexcharts-canvas");
    if (!chartElement) return;

    const canvas = await html2canvas(chartElement, {
      scale: 2,
      backgroundColor: "#fff",
      useCORS: true,
    });

    const link = document.createElement("a");
    link.download = `${title || "chart"}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };
  return (
    <div className="card h-100">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center overall-text mb-2 p-2">
        <h5>{title}</h5>
        <div className="d-flex align-items-center">
          {/* Zoom Button */}
          <button className="border-0 bg-transparent me-2" data-bs-toggle="modal" data-bs-target={`#${modalId}`}>
            <img src={ZOOM_ICON} alt="zoom" />
          </button>

          {/* Download Dropdown */}
          <div className="dropdown">
            <button className="icon-btn bg-transparent" type="button" data-bs-toggle="dropdown">
              <img src={MENU_ICON} alt="menu" />
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li><button className="dropdown-item" onClick={exportSummaryToExcel}>Download Excel</button></li>
              <li><button className="dropdown-item" onClick={exportAsPDF}>Export PDF</button></li>
              <li><button className="dropdown-item" onClick={exportAsImage}>Download Image</button></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Heatmap */}
      <div id="overall_heatmap" className="p-2">
        {loading ? (
          <div className="loader-container"><div className="loader"></div></div>
        ) : (
          <>
            <div className="legend-row mb-3">
              {colorRanges.map((item, index) => (
                <div key={index} className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: item.colorName }}></div>
                  <span className="legend-label">{`${item.rangeFrom}% - ${item.rangeTo}%`}</span>
                </div>
              ))}
            </div>

            <div className="header-row d-flex">
              {heatmapData?.map((item, index) => (
                <div key={index} className="header-cell text-center p-2 flex-fill" style={{ backgroundColor: getColorForPercentage(item.percentage) }}>
                  <p>{item.dimensionName}</p>
                  <p>{item.percentage?.toFixed(2)}%</p>
                </div>
              ))}
            </div>

            <div className="data-row d-flex mt-2">
              {heatmapData?.map((item) => (
                <div key={item.id} className="column flex-fill">
                  {item.subDimensionOverallHeatmapChartResponseList?.map((sub) => (
                    <div key={sub.id} className="data-cell p-2 text-center" style={{ backgroundColor: getColorForPercentage(sub?.percentage) }}>
                      <p>{sub?.subDimensionName}</p>
                      <p>{sub?.percentage?.toFixed(2)}%</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="d-flex justify-content-end mt-5">
        <button
          title="Add Comment"
          onClick={() => {
            setShowRemarkModal(true);
            setTimeout(() => {
              if (modalRef.current) modalRef.current.click();
            }, 0);
          }}
          className="border-0 bg-transparent position-absolute "
          style={{
            bottom: "10px",
            right: "10px",
          }}
        >
          <img src={CHAT_ICON} alt="chat" />
        </button>
      </div>
      {/* Remark Modal */}
      {showRemarkModal && (
        <Modal modalClick={modalRef} title="Heatmap Remark" buttonLable="">
          <Remark
            selectedCompanyId={selectedCompanyId}
            selectedCampaignId={selectedCampaignId}
            type={type}
            graphName={graphName}
            initialRemark={remark || ""}
            remarkId={remarkId || ""}
            isAttempt={isAttempt || 0}
            setRemarkprops={setRemark}
            setRemarkIdProps={setRemarkId}
            onCloseModal={() => setShowRemarkModal(false)}
          />
        </Modal>
      )}
      {/* HTML Zoom Modal */}
      <HtmlZoomModal id={modalId} title="Heat Map Zoom" elementId="overall_heatmap" />
    </div>
  );
};

export default Dimension;
